import React, { useContext } from "react";
import { IssuesWrapper } from "@vp/gatsby-pages-issues-tracker-plugin";
import loadable, { LoadableComponent } from "@loadable/component";
import { TabContent, TabsContents, TabsHeaders, Tabs, BoundedContent } from "@vp/swan";

//PP Tabs subcomponents
import OverviewWrapper from "./tabs/OverviewWrapper";

// Types
import {
  ResolvedTab,
  ContentfulPageProductPageV2,
  ContentfulSimpleLink,
  AccessoriesTab,
} from "../types/ContentfulTypes";

// Utils
import ErrorBoundary from "./ErrorBoundary";
import { Translations } from "../utils/Translation";
import { SocialCurationsHeader } from "./tabs/SocialCurations/SocialCurationsHeader";
import { FAQsHeader } from "./tabs/FAQs/FAQsHeader";
import { FinishingOptionsHeader } from "./tabs/FinishingOptions/FinishingOptionsHeader";
import { TranslatedHeader } from "./ProductPageTabHeaders";
import { showAccessoryTab, tabHeaderTranslations } from "../utils/Tabs";
import { ProductBuilderSelection } from "../types/ProductBuilderTypes";
import TrackingUtil from "../utils/tracking";
import { isPhotobooks } from "../utils/photobooksUtil";
import { ProductPageTabContext } from "../contexts/ProductPageTabContext";
import { ProductContext } from "../contexts/ProductContextProvider";
import { PageStaticDataContext } from "../contexts/PageStaticDataContextProvider";

const Markdown = loadable(() => import("./tabs/Markdown"));
const DesignSpecifications = loadable(() =>
  import("./tabs/DesignSpecifications/DesignSpecifications")
);
const SocialCurations = loadable(() => import("./tabs/SocialCurations"));
const FinishingOptions = loadable(() => import("./tabs/FinishingOptions"));
const FAQs = loadable(() => import("./tabs/FAQs"));
const EditorOptions = loadable(() => import("./tabs/EditorOptions"));
const MailingListTemplate = loadable(() => import("./tabs/MailingListTemplate"));
const Accessories = loadable(() => import("./tabs/Accessories"));

interface ProductPageTabsProps {
  pageData: ContentfulPageProductPageV2;
  simpleLinks: { [key: string]: ContentfulSimpleLink };
  productBuilderOptions: ProductBuilderSelection[];
}

const ProductPageTabs = (props: ProductPageTabsProps) => {
  const { pageData, productBuilderOptions } = props;
  const { selectedTabId, selectedTabFocus, setSelectedTabId, setSelectedTabFocus } = useContext(
    ProductPageTabContext
  );
  const { locale, mpv, tabData } = useContext(PageStaticDataContext);
  const { clientSideStateInitialized } = useContext(ProductContext);
  const { mpvId } = mpv;

  const tabHeaderComponents: Record<string, (props: any) => JSX.Element | null> = {
    overview: TranslatedHeader,
    design_specifications: TranslatedHeader,
    social_curations: SocialCurationsHeader,
    size: TranslatedHeader,
    dimensions: TranslatedHeader,
    price_chart: TranslatedHeader,
    tabfinishingoptions: FinishingOptionsHeader,
    taboverview: TranslatedHeader,
    tabfaqs: FAQsHeader,
    editor_options: TranslatedHeader,
    mailing_list_template: TranslatedHeader,
    mailing_service: TranslatedHeader,
    accessories: TranslatedHeader,
    finishing_options: FinishingOptionsHeader,
  };

  const tabComponents: Record<string, React.FC<any> | LoadableComponent<any>> = {
    overview: OverviewWrapper,
    design_specifications: DesignSpecifications,
    social_curations: SocialCurations,
    size: Markdown,
    dimensions: Markdown,
    price_chart: Markdown,
    tabfinishingoptions: FinishingOptions,
    taboverview: OverviewWrapper,
    tabfaqs: FAQs,
    editor_options: EditorOptions,
    mailing_list_template: MailingListTemplate,
    mailing_service: OverviewWrapper,
    accessories: Accessories,
    finishing_options: FinishingOptions,
  };

  const getComponentName = (type?: string) => {
    return type?.toLowerCase()?.replace(/ /g, "_") ?? "";
  };

  const showTab = (mpvId: string, tabType?: string, tabData?: ResolvedTab) => {
    if (tabType === "Price Chart") {
      return isPhotobooks(mpvId);
    }

    if (tabType === "Accessories") {
      return showAccessoryTab((tabData?.value as unknown) as AccessoriesTab);
    }

    return true;
  };

  if (tabData && tabData.length > 0) {
    return (
      <BoundedContent>
        <Tabs
          id="product-page-tabs"
          defaultSelectedTabId={"tab-0"}
          selectedTabId={selectedTabId}
          onRequestTabChange={(tabId: string | null, e: React.ChangeEvent<HTMLInputElement>) => {
            e.target.scrollIntoView({ behavior: "smooth", block: "nearest" });
            const tabIndex = parseInt(tabId?.slice(-1) || "0");
            TrackingUtil.trackTabChange(tabData[tabIndex].type!, mpvId);
            setSelectedTabId(tabId || "tab-0");
          }}
          aria-label={Translations.getInstance().getTranslation("tabsAriaLabel")}
        >
          <TabsHeaders>
            {tabData.map((tab: ResolvedTab, index) => {
              const tabType = tab?.__typename ?? tab?.type;
              const HeaderComponent = tabHeaderComponents[getComponentName(tabType)];
              if (HeaderComponent && showTab(mpvId, tabType, tab)) {
                return (
                  <HeaderComponent
                    key={index}
                    tab={tab}
                    tabIndex={`tab-${index}`}
                    pageData={pageData}
                    locale={locale}
                    selectedTabFocus={selectedTabFocus}
                    setSelectedTabFocus={setSelectedTabFocus}
                  />
                );
              }
              return null;
            })}
          </TabsHeaders>
          <TabsContents>
            {tabData.map((tab: ResolvedTab, index: number) => {
              if (!clientSideStateInitialized && selectedTabId !== `tab-${index}`) {
                return null;
              }
              const tabType = tab?.__typename ?? tab?.type;
              const TabComponent = tabComponents[getComponentName(tabType)];

              // For "See Details" link, which links to the Overview tab
              const id =
                tabType === "tabOverview"
                  ? Translations.getInstance()
                      .getTranslation("overviewTabHeader")
                      .replace(" ", "")
                  : undefined;

              if (TabComponent && showTab(mpvId, tabType, tab)) {
                return (
                  <TabContent key={tab.type} id={id} tabId={`tab-${index}`} mt={"4"}>
                    <IssuesWrapper
                      targetNodes={[tab]}
                      elementName={`${(tab || {}).type || ""} Tab`}
                    >
                      <ErrorBoundary>
                        <TabComponent
                          tabContentTitle={Translations.getInstance().getTranslationEntry(
                            tabHeaderTranslations[tab.type ?? ""]
                          )}
                          tabData={tab.value}
                          pageData={pageData}
                          productBuilderOptions={productBuilderOptions}
                          locale={locale}
                          selected={selectedTabId === `tab-${index}`}
                          simpleLinks={props.simpleLinks}
                        />
                      </ErrorBoundary>
                    </IssuesWrapper>
                  </TabContent>
                );
              }
              return null;
            })}
          </TabsContents>
        </Tabs>
      </BoundedContent>
    );
  }

  return <></>;
};

export default ProductPageTabs;
